// apps/frontend/src/lib/pipeline-preview/transformers/aggregation.ts
import type { PipelineNode } from '@/pages/pipelines/types/pipeline';
import type { ExecutionContext, TransformerResult } from '../types';
import { inferSchema } from '../utils/schema';

type AggregationFunction = 'SUM' | 'AVG' | 'COUNT' | 'MIN' | 'MAX' | 'DISTINCT_COUNT';

interface Aggregation {
  field: string;
  function: AggregationFunction;
  alias: string;
}

/**
 * Apply AGGREGATION node logic to data
 */
export async function aggregateData(
  node: PipelineNode,
  inputData: unknown[],
  _context: ExecutionContext
): Promise<TransformerResult> {
  const startTime = performance.now();

  try {
    const config = node.data.config as any;
    if (!config?.aggregations || !Array.isArray(config.aggregations)) {
      return {
        success: false,
        data: [],
        schema: { columns: [] },
        stats: { rows: 0, executionTime: 0 },
        error: 'No aggregations configured',
      };
    }

    // Handle empty input
    if (inputData.length === 0) {
      return {
        success: true,
        data: [],
        schema: { columns: [] },
        stats: { rows: 0, executionTime: Math.round(performance.now() - startTime) },
      };
    }

    const { groupBy = [], aggregations } = config;

    // Group data
    const groups = groupData(inputData, groupBy);

    // Apply aggregations to each group
    const resultData: unknown[] = [];
    for (const [groupKey, rows] of groups.entries()) {
      const resultRow: Record<string, unknown> = {};

      // Add group-by fields to result
      if (groupBy.length > 0) {
        const groupValues = parseValue(groupKey);
        groupBy.forEach((field: string, index: number) => {
          resultRow[field] = groupValues[index];
        });
      }

      // Apply each aggregation
      for (const agg of aggregations) {
        const value = applyAggregation(rows, agg);
        resultRow[agg.alias] = value;
      }

      resultData.push(resultRow);
    }

    // Infer schema from aggregated data
    const schema = inferSchema(resultData);

    const endTime = performance.now();
    return {
      success: true,
      data: resultData,
      schema,
      stats: {
        rows: resultData.length,
        executionTime: Math.round(endTime - startTime),
      },
    };
  } catch (error: any) {
    const endTime = performance.now();
    return {
      success: false,
      data: [],
      schema: { columns: [] },
      stats: { rows: 0, executionTime: Math.round(endTime - startTime) },
      error: error.message || 'Aggregation failed',
    };
  }
}

/**
 * Group data by specified fields
 */
function groupData(
  data: unknown[],
  groupBy: string[]
): Map<string, unknown[]> {
  const groups = new Map<string, unknown[]>();

  for (const row of data) {
    const rowData = row as Record<string, unknown>;

    // Create group key
    let groupKey: string;
    if (groupBy.length === 0) {
      // No grouping - all rows in single group
      groupKey = '__all__';
    } else {
      // Group by specified fields
      // Use null character \x00 as separator (cannot appear in JSON strings)
      const keyParts = groupBy.map(field => {
        const value = rowData[field];
        return value === null || value === undefined ? 'null' : String(value);
      });
      groupKey = keyParts.join('\x00');
    }

    // Add row to group
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(row);
  }

  return groups;
}

/**
 * Apply a single aggregation function to a group of rows
 */
function applyAggregation(rows: unknown[], agg: Aggregation): unknown {
  const { field, function: func } = agg;

  switch (func) {
    case 'COUNT':
      // COUNT always counts rows (field='*' is convention)
      return rows.length;

    case 'SUM': {
      const values = rows
        .map(row => (row as Record<string, unknown>)[field])
        .filter(v => typeof v === 'number' && !isNaN(v)) as number[];
      const sum = values.reduce((sum, v) => sum + v, 0);
      // Check for NaN and return null to indicate invalid result
      return isNaN(sum) ? null : sum;
    }

    case 'AVG': {
      const values = rows
        .map(row => (row as Record<string, unknown>)[field])
        .filter(v => typeof v === 'number' && !isNaN(v)) as number[];
      if (values.length === 0) return 0;
      const sum = values.reduce((sum, v) => sum + v, 0);
      const avg = sum / values.length;
      return isNaN(avg) ? null : avg;
    }

    case 'MIN': {
      const values = rows
        .map(row => (row as Record<string, unknown>)[field])
        .filter(v => typeof v === 'number' && !isNaN(v)) as number[];
      if (values.length === 0) return null;
      // Use reduce instead of spread to avoid stack overflow
      const min = values.reduce((min, val) => Math.min(min, val), values[0]);
      return isNaN(min) ? null : min;
    }

    case 'MAX': {
      const values = rows
        .map(row => (row as Record<string, unknown>)[field])
        .filter(v => typeof v === 'number' && !isNaN(v)) as number[];
      if (values.length === 0) return null;
      // Use reduce instead of spread to avoid stack overflow
      const max = values.reduce((max, val) => Math.max(max, val), values[0]);
      return isNaN(max) ? null : max;
    }

    case 'DISTINCT_COUNT': {
      const values = rows
        .map(row => (row as Record<string, unknown>)[field])
        .filter(v => v !== null && v !== undefined);
      // Use JSON.stringify to preserve type information
      const uniqueValues = new Set(values.map(v => JSON.stringify(v)));
      return uniqueValues.size;
    }

    default:
      return null;
  }
}

/**
 * Parse group key back into individual values
 */
function parseValue(groupKey: string): unknown[] {
  if (groupKey === '__all__') {
    return [];
  }

  const parts = groupKey.split('\x00');
  return parts.map(part => {
    if (part === 'null') return null;
    // Try to parse as number
    const num = Number(part);
    if (!isNaN(num) && part !== '') {
      return num;
    }
    return part;
  });
}
